import { defineStore } from "pinia";
import {
  useCrudStore,
  useDataStore,
  usePurchaseFlowStore,
  useUIStore,
  useSchemaStore,
} from "@/stores";
import { clearExperimentState } from "@/composables/useOfferingExperiment";
import { clearStepOrderExperimentState } from "@/composables/useStepOrderExperiment";
import { resetAnalyticsContext } from "@/composables/useAnalytics";

const SUPABASE_URL = import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT;

export const useAuthStore = defineStore("authStore", {
  state: () => ({
    // User session
    current_user: null,
    is_verified: false,
    invalid_session: false,
    is_logging_out: false,
    is_first_login: false,

    // Signup flow
    is_signup: false,
    active_signup_step: 1,
    signup_fields: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      password: "",
      confirm_password: "",
      message_cb: false,
      privacy_cb: false,
      consent_cb: false,
      signup_questionnaire: {
        are_you_an_accredited_investor: "",
        main_investment_goal: "",
        ira_or_savings: "",
        has_annuities: "",
        anticipated_investment_range: "",
      },
    },

    // Signin flow
    active_signin_step: 1,
    signin_fields: {
      email: "",
      password: "",
      phone: "",
    },

    // MFA
    mfa_channel: null,

    // Turnstile
    turnstile_token: "",
    is_turnstile_valid: false,
  }),
  getters: {
    current_email(state) {
      const email = state.is_signup ? state.signup_fields.email : state.signin_fields.email;
      return email?.toLowerCase()?.trim() || "";
    },
    current_password(state) {
      return state.is_signup ? state.signup_fields.password : state.signin_fields.password;
    },
    formatted_phone(state) {
      const phone = state.is_signup ? state.signup_fields.phone : state.signin_fields.phone;
      return `+1${phone?.replace(/\D/g, "") || ""}`;
    },
  },
  actions: {
    resetApplication() {
      const dataStore = useDataStore();
      const crudStore = useCrudStore();
      const purchaseFlowStore = usePurchaseFlowStore();
      const schemaStore = useSchemaStore();

      try {
        dataStore.$reset();
        crudStore.$reset();
        purchaseFlowStore.$reset();
        schemaStore.$reset();
        this.$reset();
      } catch (err) {
        console.error("Error resetting stores:", err);
      } finally {
        localStorage.removeItem("current_user");
        clearExperimentState();
        clearStepOrderExperimentState();
        resetAnalyticsContext();
      }
    },
    async validateCredentials(email, password) {
      try {
        const response = await fetch(`${SUPABASE_URL}/validate-credentials`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            origin_token: this.turnstile_token,
          },
          body: JSON.stringify({
            authType: "signin",
            sourceType: "investor",
            email: email.toLowerCase().trim(),
            password,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error);
        }

        return await response.json();
      } catch (err) {
        console.error("Error in validateCredentials:", err);
        return false;
      }
    },
    async sendVerificationCode(channel) {
      try {
        const channel_map = {
          Email: "email",
          "Text Message": "sms",
          "Phone Call": "call",
          "Skip MFA": "skip",
        };

        const to = channel === "Email" ? this.current_email : this.formatted_phone;

        const response = await fetch(`${SUPABASE_URL}/investor-mfa?type=request`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            origin_token: this.turnstile_token,
          },
          body: JSON.stringify({
            to,
            channel: channel_map[channel],
            email: this.current_email,
            password: this.current_password,
            isSignup: this.is_signup,
          }),
        });
        const result = await response.json();
        // console.log("Verification Code Result", result);
        return result;
      } catch (err) {
        console.error("Error in sendVerificationCode:", err);
        return { error: err.message };
      }
    },
    async verifyCodeAndSignin(code) {
      try {
        const channel_map = {
          Email: "email",
          "Text Message": "sms",
          "Phone Call": "call",
          "Skip MFA": "skip",
        };

        const to = this.mfa_channel === "Email" ? this.current_email : this.formatted_phone;

        const request_body = {
          to,
          code,
          channel: channel_map[this.mfa_channel],
          email: this.current_email,
          password: this.current_password,
          isSignup: this.is_signup,
        };

        // Add signup-specific fields
        if (this.is_signup) {
          request_body.phone = this.signup_fields.phone;
          request_body.first_name = this.signup_fields.first_name;
          request_body.last_name = this.signup_fields.last_name;
        }

        const response = await fetch(`${SUPABASE_URL}/investor-mfa?type=verify`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            origin_token: this.turnstile_token,
          },
          body: JSON.stringify(request_body),
        });

        const result = await response.json();

        if (!response.ok || result?.valid === false) {
          this.current_user = null;
          localStorage.removeItem("current_user");
          return result;
        }

        if (result.session_id) {
          this.current_user = result;
          localStorage.setItem("current_user", JSON.stringify(result));
        }

        return result;
      } catch (err) {
        console.error("Error in verifyCodeAndSignin:", err);
        throw err;
      }
    },
    async signOut() {
      const dataStore = useDataStore();
      try {
        if (this.current_user && dataStore?.contact) {
          await fetch(`${SUPABASE_URL}/sign-out`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              user_id: dataStore.contact.supabase_user_id,
              session_id: this.current_user.session_id,
              email: dataStore.contact.email,
              contact_id: dataStore.contact.record_id,
            },
          });
        }
      } catch (err) {
        console.error("Error in signOut:", err);
      } finally {
        localStorage.removeItem("current_user");
        clearExperimentState();
        clearStepOrderExperimentState();
        resetAnalyticsContext();
        this.current_user = null;
      }
    },
    async sendPasswordResetEmail(email) {
      try {
        await fetch(`${SUPABASE_URL}/send-password-reset-email`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            origin_token: this.turnstile_token,
          },
          body: JSON.stringify({
            email: email.toLowerCase().trim(),
          }),
        });

        // Always return true to avoid email enumeration
        return true;
      } catch (err) {
        return true;
      }
    },
    async resetPassword(token, password) {
      try {
        const response = await fetch(`${SUPABASE_URL}/reset-password`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            origin_token: this.turnstile_token,
          },
          body: JSON.stringify({ token, password }),
        });

        if (!response.ok) {
          return { tokenExpired: true };
        }

        const result = await response.json();
        // console.log("Password Reset Result", result);

        return result;
      } catch (err) {
        console.error("Error in resetPassword:", err);
        return { tokenExpired: true };
      }
    },
  },
});
